import { request, fetchJsonOmitCredentials } from './index'

/**
 * 读取当前收银台 URL 中的指定 query 参数。
 *
 * 业务流程：
 * 1. 规范化 search（把手机复制产生的 &amp; 还原成 &）
 * 2. 用 URLSearchParams 读取参数
 */
const getUrlParam = (name: string) => {
  try {
    const search = String(window.location.search || '')
      .replace(/&amp;/gi, '&')
      .replace(/&#0*38;/g, '&')
      .replace(/&#x0*26;/gi, '&')
    const params = new URLSearchParams(search)
    return params.get(name) || ''
  } catch {
    return ''
  }
}

/**
 * 每次请求时从当前 URL 读取支付 token（避免 SPA 内跳转后仍用模块加载时的空 token）。
 *
 * 业务流程：
 * 1. 依次尝试 k / merOrderNo / id
 * 2. trim，与 payin.vue 的 payToken 处理保持一致，避免首尾空格导致鉴权失败
 */
const getPayToken = () =>
  (getUrlParam('k') || getUrlParam('merOrderNo') || getUrlParam('id') || '').trim()

/**
 * 组装带 token 的 GET url（优先 query 传 k，降低 Safari 对自定义 Header 的 CORS 预检失败）。
 */
const withPayTokenQuery = (url: string) => {
  const token = getPayToken()
  if (!token) {
    return url
  }
  const join = url.includes('?') ? '&' : '?'
  return `${url}${join}k=${encodeURIComponent(token)}`
}

/**
 * GET 鉴权头：仅 POST / 旧接口使用。首屏 GET 已改 query k，避免 CORS 预检。
 */
const payTokenHeaders = (): Record<string, string> => {
  const token = getPayToken()
  return token ? { 'klicklpay-token': token } : {}
}

/**
 * 首屏/轮询 GET：只带 ?k=；credentials omit 直连（不再走 Safari iframe 桥，避免失败路径多等数秒）。
 *
 * 业务流程：
 * 1. URL 拼 query k（pmapi Query["k"]）
 * 2. fetch + omit Cookie
 * 3. 失败再回退 axios（withCredentials:false）
 */
const getWithPayToken = async (path: string, withHeader = false) => {
  const url = withPayTokenQuery(path)
  if (!withHeader) {
    try {
      return await fetchJsonOmitCredentials(url)
    } catch (e: any) {
      console.warn('[cashier] fetch omit-credentials failed, fallback axios', e?.message || e)
    }
  }
  const res = await request({
    method: 'get',
    url,
    withCredentials: false,
    headers: withHeader ? payTokenHeaders() : {}
  })
  return res?.data as any
}

/**
 * 获取支持的通道列表
 * @returns 返回通道列表
 */
export const CashChannelList = async () => {
  return await getWithPayToken('/api/cash/CashChannelList')
}

/**
 * 获取充值订单详情（含支付中轮询）；优先复用 HTML 抢跑结果。
 *
 * 业务流程：
 * 1. 若 index.html 已发起 __VGPAY_EARLY_PAYIN__，await 同一 Promise
 * 2. 仅当抢跑结果 code==200 时复用；否则回退常规 ?k= GET
 */
export const GetPayInOrderInfo = async () => {
  try {
    const early = (window as any).__VGPAY_EARLY_PAYIN__
    if (early && typeof early.then === 'function') {
      const body = await early
      ;(window as any).__VGPAY_EARLY_PAYIN__ = null
      if (body && body.code == 200) {
        return body
      }
    }
  } catch (e) {
    console.warn('[cashier] early payin reuse failed', e)
  }
  return await getWithPayToken('/api/cash/payin')
}

export const CallBackUrlTopUpOrder = async () => {
  const res = await request({
    method: 'get',
    data: {},
    url: `/api/cash/CallBackUrlTopUpOrder?orderNo=${getPayToken()}`,
    headers: {
      'klicklpay-token': getPayToken()
    }
  })
  return res?.data as any
}

export const GetFiatOrderLinkUsed = async () => {
  const res = await request({
    method: 'get',
    data: {},
    url: '/api/cash/payin/FiatOrderLinkUsed',
    headers: {
      'klicklpay-token': getPayToken()
    }
  })
  return res?.data as any
}

/**
 * 获取提现订单详情
 * @returns 返回订单信息
 */
export const GetPayOutOrderInfo = async () => {
  const res = await request({
    method: 'get',
    data: {},
    url: '/api/cash/payout',
    headers: {
      'klicklpay-token': getPayToken()
    }
  })
  return res?.data as any
}

interface ValidateInfoParams {
  channel_code: string
  payment_type: string
  account_number: string
  supplier?: string
  country_code?: string
  bank_code?: string
}

/**
 * 账户校验信息
 * @param data 校验接口参数
 * @returns 返回校验结果
 */
export const ValidateInfo = async (data: ValidateInfoParams) => {
  const res = await request({
    method: 'post',
    data,
    url: '/api/cash/ValidateInfo',
    headers: {
      'klicklpay-token': getPayToken()
    }
  })
  return res?.data as any
}

interface CreateOrderParams {
  channel_code: string
  country_code?: string
  supplier?: string
  payment_type?: string

  account_name?: string
  phone_number?: string
  bank_code?: string
  bank_number?: string
  /** WeTech 设备指纹 Token */
  device_id?: string
  /** WeTech 获取失败时上报完整响应 */
  wetech_res?: {
    code?: number
    msg?: string
    err?: string
    data?: {
      device_id?: string
    } | null
  }
}

/**
 * 创建现金充提订单
 * @param data 校验接口参数
 * @returns 返回校验结果
 */
export const CreateTopUpOrder = async (data: CreateOrderParams) => {
  const res = await request({
    method: 'post',
    data,
    url: '/api/cash/payin',
    headers: {
      'klicklpay-token': getPayToken()
    }
  })
  return res?.data as any
}

/**
 * 创建现金提现订单
 * @param data 校验接口参数
 * @returns 返回校验结果
 */
export const CreateWithdrawOrder = async (data: CreateOrderParams) => {
  const res = await request({
    method: 'post',
    data,
    url: '/api/cash/payout',
    headers: {
      'klicklpay-token': getPayToken()
    }
  })
  return res?.data as any
}

/**
 * 获取充值必填项
 * @param data 校验接口参数
 * @returns 返回校验结果
 */
export const CashierTopUpRequiredFields = async () => {
  return await getWithPayToken('/api/cash/CashierTopUpRequiredFields')
}

/**
 * 创建充值订单
 * @param data 校验接口参数
 * @returns 返回校验结果
 */
export const CreatePayIn = async (data: CreateOrderParams) => {
  const res = await request({
    method: 'post',
    data,
    url: '/api/cash/cashier/payin',
    headers: {
      'klicklpay-token': getPayToken()
    }
  })
  return res?.data as any
}

/**
 * 提交卡支付
 * @param data 校验接口参数
 * @returns 返回校验结果
 */
interface CreateCardPayParams {
  card_holder_name: string
  card_number: string
  card_cvv: string
  card_expiry_month: number
  card_expiry_year: number
  risk_token?: string
  /** WeTech 设备指纹 Token */
  device_id?: string
  /** WeTech 获取失败时上报完整响应 */
  wetech_res?: {
    code?: number
    msg?: string
    err?: string
    data?: {
      device_id?: string
    } | null
  }
  user_agent?: string

  accept_header?: string
  color_depth?: number
  java_enabled?: boolean
  java_script_enabled?: boolean
  language?: string
  screen_height?: number
  screen_width?: number
  time_zone?: number
}
export const CreateCardPay = async (data: CreateCardPayParams) => {
  const res = await request({
    method: 'post',
    data,
    url: '/api/cash/cashier/pay',
    headers: {
      'klicklpay-token': getPayToken()
    }
  })
  return res?.data as any
}
export const CreateCardPayWeb2 = async (data: CreateCardPayParams) => {
  const res = await request({
    method: 'post',
    data,
    url: '/api/cash/cashier/web2pay',
    headers: {
      'klicklpay-token': getPayToken()
    }
  })
  return res?.data as any
}

/**
 * 上报支付页来源上下文（referrer / iframe / parent_origin / user_agent）。
 */
export const ReportPaySourceContext = async (data: {
  referrer: string
  is_embedded: boolean
  page_url: string
  user_agent?: string
  parent_origin?: string
}) => {
  const res = await request({
    method: 'post',
    data: {
      ...data,
      user_agent: data.user_agent || (typeof navigator !== 'undefined' ? navigator.userAgent : ''),
      parent_origin: data.parent_origin || ''
    },
    url: '/api/cash/ReportPaySourceContext',
    headers: {
      'klicklpay-token': getPayToken()
    }
  })
  return res?.data as any
}

/**
 * 上报 Nuvei Simply Connect checkout onResult（fire-and-forget；失败不影响支付）。
 */
export const ReportNuveiCheckoutResult = (data: {
  order_no?: string
  payment_type?: string
  client_unique_id?: string
  client_request_id?: string
  result?: string
  err_code?: string | number
  error_description?: string
  transaction_id?: string
  cancelled?: boolean
  gw_error_code?: string | number
  gw_extended_error_code?: string | number
}) => {
  const toText = (value: any) => {
    if (value === undefined || value === null || value === '') return ''
    return String(value)
  }
  request({
    method: 'post',
    data: {
      order_no: toText(data.order_no),
      payment_type: toText(data.payment_type),
      client_unique_id: toText(data.client_unique_id),
      client_request_id: toText(data.client_request_id),
      result: toText(data.result),
      err_code: toText(data.err_code),
      error_description: toText(data.error_description),
      transaction_id: toText(data.transaction_id),
      cancelled: !!data.cancelled,
      gw_error_code: toText(data.gw_error_code),
      gw_extended_error_code: toText(data.gw_extended_error_code)
    },
    url: '/api/cash/ReportNuveiCheckoutResult',
    headers: {
      'klicklpay-token': getPayToken(),
      'Content-Type': 'application/json'
    }
  }).catch((e: any) => {
    console.warn('[cashier] ReportNuveiCheckoutResult failed', e?.message || e)
  })
}

/**
 * 获取收银台前端嵌入策略（iframe 软拦；对齐 FrameAncestorsMode / 白名单）。
 *
 * 业务流程：
 * 1. 仅用 ?k= 拉策略，避免移动端 CORS 预检
 * 2. 失败由调用方按 iframe/顶层策略降级
 */
export const GetCashierEmbedPolicy = async () => {
  return await getWithPayToken('/api/cash/GetCashierEmbedPolicy')
}

/**
 * 获取提现必填项
 * @param data 校验接口参数
 * @returns 返回校验结果
 */
export const CashierWithdrawRequiredFields = async () => {
  const res = await request({
    method: 'get',
    url: '/api/cash/CashierWithdrawRequiredFields  ',
    headers: {
      'klicklpay-token': getPayToken()
    }
  })
  return res?.data as any
}

/**
 * 创建提现订单
 * @param data 校验接口参数
 * @returns 返回校验结果
 */
export const CreatePayOut = async (data: CreateOrderParams) => {
  const res = await request({
    method: 'post',
    data,
    url: '/api/cash/cashier/payout',
    headers: {
      'klicklpay-token': getPayToken()
    }
  })
  return res?.data as any
}